import React, { useEffect, useState } from 'react';
import './_NewBranch.css';
import { BranchesModel } from '../models/BranchesModel';
import { BranchModel, ActivityStatus, SelectListItem } from '../models/superbit-api-models';

// React port of Views/SystemManagement/Modals/_NewBranch.cshtml.
// Handles add-branch, add-department (parentId set) and edit (branch set). All
// three save through brachUpsert — the server upserts by Id, exactly like the old
// form which always posted to Branch/Upsert.
//
// A "department" is just a branch with a parent. The old form hid the address and
// logo fields and switched the labels (סניף -> מחלקה) whenever a parentId was set.

type Props = {
    show: boolean;
    // Existing branch when editing; undefined when adding.
    branch?: BranchModel;
    // Parent id when adding a department under a branch; undefined for a top-level branch.
    parentId?: string;
    onClose: () => void;
    onSaved: () => void;
};

type FormState = {
    id?: string;
    name: string;
    address: string;
    status: ActivityStatus;
    branchManagerId: string;
    parentId?: string;
    logo: string;
};

const NewBranch = (props: Props) => {
    const { show, branch, parentId, onClose, onSaved } = props;
    const isEdit = !!(branch && branch.id);

    const [branchesModel] = useState<BranchesModel>(() => new BranchesModel());

    const [form, setForm] = useState<FormState>({
        name: '',
        address: '',
        status: ActivityStatus._0,
        branchManagerId: '',
        logo: '',
    });
    const [managers, setManagers] = useState<SelectListItem[]>([]);
    const [errors, setErrors] = useState<{ [k: string]: string }>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');

    // A department is identified by having a parent (either the one we're adding
    // under, or — when editing — the parentId already on the branch).
    const effectiveParentId = isEdit ? branch?.parentId : parentId;
    const isDepartment = !!effectiveParentId;

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setServerError('');
        // Seed the form from the branch being edited, or a blank add form.
        setForm({
            id: branch?.id,
            name: branch?.name || '',
            address: branch?.address || '',
            status: branch?.status != null ? branch.status : ActivityStatus._0,
            branchManagerId: branch?.branchManagerId || '',
            parentId: effectiveParentId,
            logo: branch?.logo || '',
        });
        loadManagers();
    }, [show, branch, parentId]);

    const loadManagers = async () => {
        try {
            const list = await branchesModel.getBranchManagers();
            setManagers(list);
        } catch (e) {
            console.error('Failed to load branch managers', e);
            setManagers([]);
        }
    };

    const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

    const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => update({ logo: typeof reader.result === 'string' ? reader.result : '' });
        reader.readAsDataURL(file);
    };

    const validate = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!form.name.trim()) errs.name = 'שדה חובה';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServerError('');
        if (!validate()) return;

        const body = new BranchModel();
        body.id = form.id;
        body.name = form.name.trim();
        body.status = form.status;
        body.branchManagerId = form.branchManagerId || undefined;
        body.parentId = form.parentId;
        // Address and logo only apply to branches, not departments (matching the old form).
        if (!isDepartment) {
            body.address = form.address;
            body.logo = form.logo || undefined;
        }

        setSaving(true);
        try {
            await branchesModel.branchUpsert(body);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save branch', err);
            setServerError('שמירת הסניף נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const entity = isDepartment ? 'מחלקה' : 'סניף';
    const title = isEdit
        ? isDepartment
            ? 'עריכת מחלקה'
            : 'עריכת סניף'
        : isDepartment
        ? 'מחלקה חדשה'
        : 'סניף חדש';

    return (
        <div className="newbranch-overlay" onClick={handleOverlayClick}>
            <div className="newbranch-modal" dir="rtl">
                <form id="addBranchForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">{title}</h3>
                    </div>

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">שם ה{entity}</span>
                            </div>
                            <input
                                className="form-control"
                                placeholder="שם"
                                value={form.name}
                                onChange={(e) => update({ name: e.target.value })}
                            />
                            {errors.name && <span className="text-danger">{errors.name}</span>}
                        </div>
                    </div>

                    {!isDepartment && (
                        <div className="create-form__wrappers">
                            <div className="col-sm">
                                <div className="minor-title-box text-right">
                                    <span className="title">כתובת</span>
                                </div>
                                <input
                                    className="form-control"
                                    placeholder="כתובת"
                                    value={form.address}
                                    onChange={(e) => update({ address: e.target.value })}
                                />
                            </div>
                        </div>
                    )}

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">סטטוס</span>
                            </div>
                            <select
                                className="form-control"
                                value={form.status}
                                onChange={(e) =>
                                    update({ status: parseInt(e.target.value, 10) as ActivityStatus })
                                }
                            >
                                <option value={ActivityStatus._0}>פעיל</option>
                                <option value={ActivityStatus._1}>לא פעיל</option>
                            </select>
                        </div>
                    </div>

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">מנהל {entity}</span>
                            </div>
                            <select
                                className="form-control"
                                value={form.branchManagerId}
                                onChange={(e) => update({ branchManagerId: e.target.value })}
                            >
                                <option value="">בחר</option>
                                {managers.map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {!isDepartment && (
                        <div className="create-form__wrappers">
                            <div className="col-sm">
                                <div className="minor-title-box text-right">
                                    <span className="title">לוגו</span>
                                </div>
                                {form.logo ? (
                                    <div className="newbranch-logo">
                                        <img src={form.logo} alt="logo" />
                                        <span className="remove-logo" onClick={() => update({ logo: '' })}>
                                            הסר
                                        </span>
                                    </div>
                                ) : (
                                    <input
                                        className="form-control"
                                        type="file"
                                        accept="image/*"
                                        onChange={onLogoChange}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {serverError && <div className="newbranch-server-error text-danger">{serverError}</div>}

                    <div className="action-buttons">
                        <div className="action-buttons__half">
                            <button
                                type="submit"
                                className="btn btn-link button btn-primary button-outlined"
                                disabled={saving}
                            >
                                {saving ? 'שומר…' : isEdit ? 'שמור' : 'הוסף'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-link button button-outlined"
                                onClick={onClose}
                            >
                                ביטול
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewBranch;
